import type { SearchHit, SearchResults } from '@core/db';
import { timingOf, timingText } from '@core/ui/TaskTiming';

/**
 * Выдача поиска — «Структура» §10.
 *
 * Категории идут в одном и том же порядке: дела, задачи, документы, клиенты
 * ([D-15](docs/DECISIONS.md)). Порядок постоянный, потому что глаз запоминает место,
 * а не заголовок: перестановка категорий по числу совпадений заставляла бы читать
 * выдачу заново каждый раз.
 */
export type SearchCategory = 'cases' | 'tasks' | 'documents' | 'clients';

export const CATEGORY_TITLE: Record<SearchCategory, string> = {
  cases: 'Дела',
  tasks: 'Задачи',
  documents: 'Документы',
  clients: 'Клиенты',
};

const ORDER: SearchCategory[] = ['cases', 'tasks', 'documents', 'clients'];

export interface SearchResultItem {
  id: string;
  title: string;
  /** Клиент для дела, срок и дело для задачи, дело для документа, телефон для клиента. */
  subtitle: string | null;
  category: SearchCategory;
  caseId: string | null;
  /** Избранный документ помечается звездой и в поиске — [D-20](docs/DECISIONS.md). */
  isFavorite: boolean;
}

export type SearchListEntry =
  | { kind: 'header'; category: SearchCategory }
  | { kind: 'item'; result: SearchResultItem };

export interface SearchState {
  query: string;
  entries: SearchListEntry[];
  /**
   * До первого символа показываем недавнее, а не пустоту: чаще всего юрист
   * возвращается к тому, с чем работал сегодня ([D-11](docs/DECISIONS.md)).
   */
  isRecent: boolean;
  /** Запрос из одной буквы: ищем от двух, и об этом надо сказать, а не молчать. */
  isTooShort: boolean;
}

/** Срок задачи в выдаче набирается тем же форматтером, что и в списке задач. */
function taskSubtitle(hit: SearchHit, now: number): string | null {
  if (hit.at === null || hit.at === undefined) return hit.subtitle;

  const timing = timingText(hit.at, timingOf(hit.at, false, now));
  return hit.subtitle ? `${hit.subtitle} · ${timing}` : timing;
}

const toItem = (hit: SearchHit, category: SearchCategory, now: number): SearchResultItem => ({
  id: hit.id,
  title: hit.title,
  subtitle: category === 'tasks' ? taskSubtitle(hit, now) : hit.subtitle,
  category,
  caseId: hit.caseId ?? null,
  isFavorite: hit.isFavorite ?? false,
});

/** Выдача в строки списка: заголовок категории и её результаты, пустые пропускаются. */
export function entriesOf(results: SearchResults, now: number): SearchListEntry[] {
  return ORDER.flatMap((category): SearchListEntry[] => {
    const hits = results[category];
    if (hits.length === 0) return [];

    return [
      { kind: 'header', category },
      ...hits.map((hit): SearchListEntry => ({ kind: 'item', result: toItem(hit, category, now) })),
    ];
  });
}
